import { REST_API, Permissions } from '@awp/constants';
import { GetJson, PostJson, PutJson, DeleteJson } from '@awp/helpers/fetchReact';
import * as Actions from '../Helpers/Actions';
import { useDispatch } from 'react-redux';

// Helper function to group PersistentGraphFilters by WeatherStationId
/**
 * Groups an array of persistent graph filters by their associated weather station IDs.
 *
 * @function
 * @param {Array<Object>} filters - An array of persistent graph filter objects.
 * @returns {Object} An object where keys are weather station IDs and values are arrays of associated filters.
 */
export const groupFiltersByWeatherStation = (filters) => {
  /**
   * The function takes an array of persistent graph filter objects and organizes them into an object.
   * The keys of the object represent weather station IDs, and the values are arrays of associated filters.
   * Filters with the same weather station ID are grouped together in the same array.
   *
   * @param {Array<Object>} result - An accumulator object to store grouped filters.
   * @param {Object} filter - The current persistent graph filter object being processed.
   * @returns {Object} An updated accumulator object with grouped filters.
   */
  return filters.reduce((result, filter) => {
    const weatherStationId = filter.WeatherStationId;

    if (!result[weatherStationId]) {
      result[weatherStationId] = [];
    }

    result[weatherStationId].push(filter);
    return result;
  }, {});
};

/**
 * Saves a preset data asynchronously, dispatching an action to update the state.
 *
 * @param {Object} presetData - The data of the preset to be saved.
 * @returns {Promise<boolean>} - A Promise that resolves to true if the preset is successfully saved, or false otherwise.
 */
export const savePreset = async (presetData, dispatch) => {
  // Define the URL for the REST API endpoint to save the preset
  const url = REST_API.GRAPH_PRESETS;

  // Extract the preset ID from the preset data
  const presetId = presetData.GraphPresetId;

  try {
    let response;

    if (presetId === -1) {
      delete presetData.GraphPresetId;
      //create
      response = await PostJson(url, presetData);
    } else {
      //update
      response = await PutJson(url, presetData);
    }

    // Check if the response status is OK (HTTP status code 200)
    if (response.ok) {
      // Parse the response as JSON
      const savedPresetPromise = response.json();
      const savedPreset = await savedPresetPromise;

      // Dispatch an action to save the preset data to the state
      await dispatch(Actions.savePreset(savedPreset, presetId));

      // Return true to indicate a successful save
      return true;
    } else if (response.status === 412) {
      // If the response status is 412 (Precondition Failed), handle it accordingly.
      // TODO: You may want to throw an error, log the error, or perform specific actions.
      return false;
    } else {
      // Handle other response errors by throwing an error with a descriptive message
      throw new Error(`Failed to save preset data: ${response.statusText}`);
    }
  } catch (error) {
    // Handle network or JSON parsing errors by throwing an error with a descriptive message
    throw new Error(`Error saving preset data: ${error.message}`);
  }
};

/**
 * Saves a preset data asynchronously, dispatching an action to update the state.
 *
 * @param {Object} presetData - The data of the preset to be saved.
 * @returns {Promise<boolean>} - A Promise that resolves to true if the preset is successfully saved, or false otherwise.
 */
export const deletePreset = async (graphPresetId, dispatch) => {
  // Define the URL for the REST API endpoint to delete the preset
  const url = REST_API.GRAPH_PRESETS + graphPresetId;

  try {
    // Send a GET request to the specified URL using the GetJson function
    const response = await DeleteJson(url);

    // Check if the response status is OK (HTTP status code 200)
    if (response.ok) {
      // Parse the response as JSON
      const wasPresetDeleted = response.json();

      if (wasPresetDeleted) {
        // Dispatch an action to save the preset data to the state
        dispatch(Actions.deletePreset(graphPresetId));

        // Return true to indicate a successful save
        return true;
      }
    } else if (response.status === 412) {
      // If the response status is 412 (Precondition Failed), handle it accordingly.
      // TODO: You may want to throw an error, log the error, or perform specific actions.
    } else {
      // Handle other response errors by throwing an error with a descriptive message
      throw new Error(`Failed to save preset data: ${response.statusText}`);
    }
  } catch (error) {
    // Handle network or JSON parsing errors by throwing an error with a descriptive message
    throw new Error(`Error saving preset data: ${error.message}`);
  }

  // Return false by default, indicating a failure to save the preset
  return false;
};

/**
 * Asynchronously fetches weather station data for a specified weather station ID from the REST API.
 *
 * @async
 * @function
 * @param {number} weatherStationId - The ID of the weather station to fetch data for.
 * @returns {Promise<Array<Object>>} An array of formatted weather station data options with label, value, and placementName properties.
 * @throws {Error} If there is an error during the fetch operation or data processing.
 */
export const getWeatherStationData = async (weatherStationId) => {
  // Define the URL to fetch weather station data from based on the provided weatherStationId
  //const url = `${REST_API.WEATHER_STATIONS}${weatherStationId}/sensorequipmentpacket`;
  const url = `${REST_API.GRAPH_PRESETS}observations/${weatherStationId}`;

  try {
    // Send an HTTP GET request to the specified URL and wait for the response
    const response = await GetJson(url);

    // Check if the response status is OK (HTTP status code 200)
    if (response.ok) {
      // Parse the JSON response into a weather station object
      const observations = await response.json();

      // Check if weather station data is available
      if (observations) {
        // Format the weather station data into options for dropdown selection
        const options = observations.reduce((result, item) => {
          // Check if a parent option with the same PlacementName already exists
          const parentOption = result.find((option) => option.label === item.PlacementName);

          if (parentOption) {
            // Add a child item to the existing parent option
            parentOption.options.push({
              label: item.Precedence //&& !(item.ObservationTypeLabel + '').endsWith('Q#')
                ? `${item.ObservationTypeLabel} (${item.Precedence})`
                : item.ObservationTypeLabel,
              value: item.DatasetId,
              placementName: item.PlacementName,
              id: item.ObservationTypeId,
              object: item,
            });
          } else {
            // Create a new parent option and add the child item
            result.push({
              label: item.PlacementName,
              options: [
                {
                  label: item.Precedence //&& !(item.ObservationTypeLabel + '').endsWith('Q#')
                    ? `${item.ObservationTypeLabel} (${item.Precedence})`
                    : item.ObservationTypeLabel,
                  value: item.DatasetId,
                  placementName: item.PlacementName,
                  id: item.ObservationTypeId,
                  color: item.DefaultColor,
                  object: item,
                },
              ],
            });
          }

          return result;
        }, []);

        // Sort the options array alphabetically based on the 'label' property
        options.sort((a, b) => a.label.localeCompare(b.label));

        // Return the formatted weather station data options
        return options;
      } else {
        // If weather station data is not available, return an empty array
        return [];
      }
    } else if (response.status === 412) {
      // If the response status is 412 (Precondition Failed), return an empty array
      return [];
    } else {
      // Handle other response errors as needed by throwing an error
      throw new Error(`Failed to fetch weather station data: ${response.statusText}`);
    }
  } catch (error) {
    // Handle network or JSON parsing errors by throwing an error with a descriptive message
    throw new Error(`Error fetching weather station data: ${error.message}`);
  }
};

/**
 * Asynchronously fetches observation data from the REST API.
 *
 * @async
 * @function
 * @returns {Promise<Array<Object>>} An array of observation data objects with label and value properties.
 * @throws {Error} If there is an error during the fetch operation or data processing.
 */
export const getObservationData = async () => {
  // Define the URL to fetch observation data from
  const url = `${REST_API.GRAPH_PRESETS}observations`;

  try {
    // Send an HTTP GET request to the specified URL and wait for the response
    const response = await GetJson(url);

    // Check if the response status is OK (HTTP status code 200)
    if (response.ok) {
      // Parse the JSON response into an array of observation objects
      const observations = await response.json();

      // Check if observations data is available
      if (observations) {
        // Sort the observation data alphabetically by DisplayName
        const observationData = observations
          //.sort((a, b) => (a.DisplayName > b.DisplayName ? 1 : -1))
          .map((obs) => {
            ///   frost probe/snow pole needs elevation
            // Transform each observation object into a label-value pair
            return {
              label: obs.ObservationTypeLabel,
              value: obs.DatasetId,
              id: obs.ObservationTypeId,
              color: obs.DefaultColor,
              object: obs,
            };
          });

        // Return the processed observation data
        return observationData;
      } else {
        // If observations data is not available, return an empty array
        return [];
      }
    } else if (response.status === 412) {
      // If the response status is 412 (Precondition Failed), return an empty array
      return [];
    } else {
      // Handle other response errors as needed by throwing an error
      throw new Error(`Failed to fetch observation data: ${response.statusText}`);
    }
  } catch (error) {
    // Handle network or JSON parsing errors by throwing an error with a descriptive message
    throw new Error(`Error fetching observation data: ${error.message}`);
  }
};

/**
 * Finds the selected option within a list of options based on the provided selected value.
 *
 * @param {Array} options - An array of options to search within.
 * @param {any} selectedValue - The value of the selected option to find.
 * @param {string|null} placementName - The group that the selected option exists in for datasets with a 0 prefix or the actual name for others like images
 * @param {string|null} datasetPrefix - The dataset prefix defines the type of of dataset we are dealing with i.e. 1 = Camera Images
 * @returns {Object | null} The selected option object if found, or null if not found.
 */
export const findSelectedOption = (options, selectedValue, placementName, datasetPrefix) => {
  if (Array.isArray(options) && options.length > 0 && options[0].options) {
    // Check if the options are grouped
    for (const group of options) {
      let selectedOption = null;

      if (group.label === placementName) {
        // Iterate through groups of options
        selectedOption = group.options.find((option) => option.id === selectedValue);
      }
      //are we dealing with a camera group?
      if (datasetPrefix === '1' && group.label === 'Images') {
        selectedOption = group.options.find((option) => option.label === placementName);
      }
      // Attempt to find the selected option within the current group
      if (selectedOption) {
        // If the selected option is found in the group
        return selectedOption;
      }
    }
  } else {
    // Options are not grouped
    const selectedOption = options.find((option) => option.id === selectedValue);
    // Attempt to find the selected option within the ungrouped options
    if (selectedOption) {
      // If the selected option is found among the ungrouped options
      return selectedOption;
    }
  }

  // If the selected value is not found in any options
  return null;
};

export const getManagePermissions = (currentUser) => {
  const canManagePublic = currentUser.Permissions.findIndex((p) => p === 'WRITE_PUBLIC_PRESET') >= 0;
  const canManagePrivate = currentUser.Permissions.findIndex((p) => p === 'WRITE_CUSTOM_GRAPH') >= 0;

  return { canManagePublic, canManagePrivate };
};
